import React, { useState } from 'react';
import { Calendar, Shield, CheckCircle, Clock, Eye, EyeOff, X } from 'lucide-react';

export default function LicenseCard({ license, validityStatus }) {
  const [isOpen, setIsOpen] = useState(false);

  // const license = {
  //   LicenseID: "97bfcced-2850-478b-ad4e-4527dcc9a880",
  //   ClientID: "CLI-0009",
  //   StartDate: "2025-11-19T17:34:00Z",
  //   EndDate: "2026-11-19T17:34:00Z",
  //   Status: "ACTIVE",
  //   CreatedBy: "oluwabukanla06@gmail.com",
  //   RevokedBy: null,
  //   RevokedAt: null,
  //   CreatedAt: "2025-11-19T17:35:01.48668Z"
  // };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!license?.EndDate) return 0;
    const endDate = new Date(license.EndDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (!license) return null;

  const daysRemaining = getDaysRemaining();
  const isExpiringSoon = daysRemaining < 30 && daysRemaining > 0;

  return (
    <div className="flex items-center justify-center w-full">
      <div className="w-full p-8 inset-0 rounded-2xl">
        {/* Main Card */}
        <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[900px]' : 'max-h-[160px]'}`}>
          
          {/* Header - Always Visible */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-6 text-white cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
          {/* <div className="bg-primary px-6 py-6 text-white"> */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 justify-center">
                <Shield size={32} className="text-emerald-100" />
                <h2 className="text-xl font-bold text-white m-0 p-0 py-4 leading-none">License</h2>
              </div>

              {
                validityStatus && license.Status === "ACTIVE" ? (
                  <div className={`flex items-center gap-1 bg-white w-fit px-3 py-1 rounded-full`}>
                    <CheckCircle size={16} className="text-emerald-700" />
                    <span className="text-sm font-semibold text-emerald-700 m-0 p-0">ACTIVE</span>
                  </div>
                ) : (
                  <div className={`flex items-center gap-1 bg-white w-fit px-3 py-1 rounded-full`}>
                    <X size={16} className="text-primary-error" />
                    <span className="text-sm font-semibold text-primary-error m-0 p-0">INACTIVE</span>
                  </div>
                )
              }

              {/* <div className={`flex items-center gap-1 bg-white w-fit px-3 py-1 rounded-full`}>
                <CheckCircle size={16} className="text-primary" />
                <span className="text-sm font-semibold text-emerald-700 m-0 p-0">{license.Status}</span>
              </div> */}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 bg-white/20 w-fit px-3 py-1 rounded-full">
                <Clock size={16} className={isExpiringSoon ? 'text-red-600' : 'text-white-600'} />
                <span className={`text-sm font-semibold ${isExpiringSoon ? 'text-red-700' : 'text-white-700'}`}>
                  {daysRemaining} days remaining
                </span>
              </div>
              {/* <p className="text-red-100 text-sm py-2 m-0">License ID: {license?.LicenseID.slice(0, 8)}...</p> */}
              <button 
                  className="hover:bg-emerald-600 rounded-full transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                >
                  {isOpen ? <EyeOff size={24} /> : <Eye size={24} />}
              </button>
            </div>
          </div>

          {/* Content - Expandable */}
          <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 h-0'}`}>
            {/* Content */}
            <div className="px-6 py-6 space-y-6">
              {/* Client ID */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                <span className="text-slate-600 text-sm font-medium">Client ID</span>
                <span className="text-slate-900 font-semibold">{license.ClientID}</span>
              </div>

              {/* Date Range */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase">Start Date</p>
                    <p className="text-slate-900 font-semibold">{formatDate(license.StartDate)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar size={18} className={`${isExpiringSoon ? 'text-amber-500' : 'text-emerald-500'} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase">End Date</p>
                    <p className="text-slate-900 font-semibold">{formatDate(license.EndDate)}</p>
                  </div>
                </div>
              </div>

              {/* Days Remaining */}
              <div className={`rounded-lg p-3 ${isExpiringSoon ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                <div className="flex items-center gap-2">
                  <Clock size={16} className={isExpiringSoon ? 'text-amber-600' : 'text-emerald-600'} />
                  <span className={`text-sm font-semibold ${isExpiringSoon ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {daysRemaining} days remaining
                  </span>
                </div>
              </div>

              {/* Created Info */}
              <div className="pt-4 border-t border-slate-200 space-y-2">
                <p className="text-slate-600 text-xs font-medium uppercase">Created By</p>
                <p className="text-slate-900 text-sm font-medium break-all">{license.CreatedBy}</p>
                <p className="text-slate-500 text-xs">{formatDate(license.CreatedAt)}</p>
              </div>

              {/* Footer Status */}
              <div className="bg-slate-50 px-4 py-3 -mx-6 -mb-6 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 ${validityStatus && license.Status ? "bg-emerald-500" : "bg-primary-error"} rounded-full animate-pulse`}></div>
                  <span className="text-slate-700 text-sm font-medium">
                    License is {validityStatus && license.Status ? "active and valid" : "inactive or invalid"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
